import { z } from 'zod';
/** RFC 9457 problem details, the error body of every platform-api endpoint. */
export const ERROR_CODES = [
    'bad_request',
    'validation_failed',
    'unauthenticated',
    'token_invalid',
    'token_expired',
    'forbidden',
    'not_found',
    'conflict',
    'rate_limited',
    'unknown_host',
    'origin_not_allowed',
    'precondition_failed',
    'dependency_unavailable',
    'invitation_expired',
    'invitation_revoked',
    'invitation_already_used',
    'invitation_email_mismatch',
    // The organization requires its own sign-in (SSO connection) and this session came through another
    // one. The problem's hints say where to sign in; nothing was changed.
    'sso_required',
    'internal',
];
/**
 * Identity-provider hints that come with sso_required: an Auth0 organization id and a connection name.
 * Opaque references for restarting sign-in, not secrets.
 */
export const problemHintsSchema = z.object({
    organization: z.string().optional(),
    connection: z.string().optional(),
});
/**
 * Why a request was refused, so a client can choose the way forward without reading the detail text.
 *
 * Staff access (403): staff_required (not active Trace staff: another account is needed) or mfa_required (staff,
 * but this session was not established with multi-factor authentication: signing in again with it can change the
 * answer).
 *
 * 0.8.0, organization administration (/v1/organizations/{orgId}/...):
 * - self_change (409): nobody changes their own membership or roles, invites their own address or accepts an
 *   invitation they sent; another administrator does it.
 * - last_owner (409): the last active org_owner can be neither removed, suspended nor demoted.
 * - owner_required (403): only an org_owner grants or removes the org_owner role (or changes an owner's membership).
 * - stopped_by_trace (403): an organization releases its own emergency stops only, never one Trace placed.
 *
 * Auth0 organization link (staff):
 * - auth0_management_unconfigured (503): this platform-api has no Auth0 Management API credentials.
 * - auth0_organization_taken (409): an Auth0 organization with the derived name exists and belongs to another
 *   Trace organization (its trace_org_id metadata differs), so it is never adopted.
 *
 * Product subscriptions (staff):
 * - subscription_exists (409): the organization already has a live subscription to that product.
 *
 * Trace staff (platform admins):
 * - person_not_found (404): no person who has signed in to Trace has that subject or email.
 * - staff_exists (409): the person is already active Trace staff.
 * - last_platform_admin (409): the change would leave no active platform admin.
 * (self_change, above, also answers a staff member changing their own staff role or status.)
 *
 * Portals (staff):
 * - customer_account_ref_exists (409): another customer account of the organization has that reference.
 *
 * Ending a trial early (staff, 0.14.0):
 * - payment_method_required (409): the customer has no card on file at the billing provider; an organization
 *   administrator adds one through Manage payment first.
 * - trial_not_endable (409): the contract is not in a trial, has an end date, or is not linked to the billing
 *   provider yet; the detail says which.
 *
 * Tickets (0.18.0):
 * - membership_inactive (403): the caller's membership of the organization is suspended or removed.
 * - attachments_unavailable (503 dependency_unavailable): this environment has no attachment storage configured;
 *   tickets without attachments work as usual.
 * - attachment_too_large (400 at presign, 422 at finalize): over TICKET_ATTACHMENT_MAX_BYTES.
 * - too_many_attachments (400): more than TICKET_ATTACHMENTS_PER_MESSAGE attachments on one message.
 * - attachment_not_uploaded (409 conflict): finalize was called before the file was uploaded.
 * - attachment_size_mismatch (422): the uploaded file is not the size declared at presign; it is rejected.
 * - attachment_type_mismatch (422): the uploaded bytes are not the declared type (for example HTML declared as
 *   image/png); it is rejected and deleted.
 * - a refused ticket transition (409 conflict): already_escalated, not_escalated, closed (a closed ticket changes
 *   only through reopen), with_trace (Trace holds the ticket, so only Trace closes it), not_resolved (nothing to
 *   reopen), reopen_window (resolved more than 14 days ago: reply to start a new ticket), triage_reserved (Triage is
 *   entered only by escalating).
 * - product_mismatch (403): an internal ticket route was called for a product that is not the calling service's own.
 * - code_taken (409): another organization already has that ticket code.
 * - code_in_use (409): a ticket was numbered with the organization's current code, so it cannot change any more.
 * - label_taken (409): a live ticket label already has that name.
 * Internal organizations (staff billing):
 * - billing_exempt (409): the organization is internal (billing exempt), so no contract is signed for it.
 * - contract_in_force (409): the organization has a contract in force or scheduled, so it cannot be made internal.

 * Self-serve sign-up (staff):
 * - signup_not_retryable (409): only a sign-up needing attention can be retried by staff.
 *
 * Start your trial (self-serve sign-up, spec 2026-10-18 piece 5):
 * - self_serve_disabled (403): self-serve sign-up is switched off in Trace Admin.
 * - email_not_verified (403): the sign-in carries no email the identity provider verified.
 * - personal_email_domain (403): a personal or disposable email provider; trials are for companies.
 * - domain_taken (409): an organization already holds the email domain; ask its administrators for an invite.
 * - domain_signup_pending (409): another person's sign-up holds the email domain.
 * - signup_started (409): the person's own sign-up is past its card.
 * - self_serve_plan_unavailable (503): no published Trace Enterprise version with a trial.
 *
 * Self-serve onboarding (internal billing, piece 6):
 * - billing_manage_required (403): the person an acting service acts for holds no billing.manage in the organization.
 *
 * New organization (staff, 0.23.0):
 * - slug_taken (409): another organization has that slug.
 * - email_domain_claimed (409): another organization claims the owner's email domain; the detail names it, and the
 *   same request with acknowledgeDomainClaim true creates the organization anyway.
 * - enterprise_provisioning_not_retryable (409): only an Enterprise provisioning needing attention can be retried.
 *
 * The agent setting (0.24.0):
 * - agent_setting_by_trace (403): an organization's administrators asked to turn a module's agent on or off or to change
 *   its highest mode; only Trace staff change those, in Trace Admin. The intelligence level stays theirs to set.
 */
export const PROBLEM_REASONS = [
    'staff_required',
    'mfa_required',
    'self_change',
    'last_owner',
    'owner_required',
    'stopped_by_trace',
    'auth0_management_unconfigured',
    'auth0_organization_taken',
    'subscription_exists',
    'person_not_found',
    'staff_exists',
    'last_platform_admin',
    'customer_account_ref_exists',
    'payment_method_required',
    'trial_not_endable',
    // 0.18.0, tickets: see the list above.
    'membership_inactive',
    'attachments_unavailable',
    'attachment_too_large',
    'too_many_attachments',
    'attachment_not_uploaded',
    'attachment_size_mismatch',
    'attachment_type_mismatch',
    'already_escalated',
    'not_escalated',
    'closed',
    'with_trace',
    'not_resolved',
    'reopen_window',
    'triage_reserved',
    'product_mismatch',
    'code_taken',
    'code_in_use',
    'label_taken',
    // Internal organizations: see the list above.
    'billing_exempt',
    'contract_in_force',
    // Self-serve sign-up: see the list above.
    'signup_not_retryable',
    // Start your trial: see the list above.
    'self_serve_disabled',
    'email_not_verified',
    'personal_email_domain',
    'domain_taken',
    'domain_signup_pending',
    'signup_started',
    'self_serve_plan_unavailable',
    // Self-serve onboarding (piece 6): see the list above.
    'billing_manage_required',
    // New organization (0.23.0): see the list above.
    'slug_taken',
    'email_domain_claimed',
    'enterprise_provisioning_not_retryable',
    // The agent setting (0.24.0): see the list above.
    'agent_setting_by_trace',
];
export const problemReasonSchema = z.enum(PROBLEM_REASONS);
export const problemSchema = z.object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    code: z.enum(ERROR_CODES),
    detail: z.string().optional(),
    correlationId: z.string().optional(),
    issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    hints: problemHintsSchema.optional(),
    reason: problemReasonSchema.optional(),
});
/** Request correlation header propagated across services and into structured logs. */
export const CORRELATION_HEADER = 'x-correlation-id';
//# sourceMappingURL=errors.js.map